import { jwtDecode } from 'jwt-decode';
import AppConfig from '../AppConfig';
import Cookies from 'js-cookie';
import { getAgentSession, getAgentToken, isAgentLaunch } from './agentSession';

const TOKEN_RETRY_DELAY_MS = 400;
const TOKEN_MAX_RETRIES = 4;

export type JWTPayload = {
  exp?: number;
  memberId: string;
  organisationId: string;
  emailId: string;
  source: 'MEMBERS' | 'IAM';
  d2cUserId: string;
  name: string;
};

// Utility functions for getServicesAPIToken
function isServer(): boolean {
  return typeof window === 'undefined';
}

async function getIdTokenForCurrentUser(): Promise<string | null> {
  // In a client-side React app, we use the token from cookies
  return getToken();
}

function getCookie(cookieName: string): string | undefined {
  return Cookies.get(cookieName);
}

function getXPlumTokenCookieNameByEnv(): string {
  return AppConfig.JWT_TOKEN_COOKIE_NAME;
}

function isTokenExpired(jwtPayload: JWTPayload): boolean {
  const now = Date.now() / 1000; // current time in seconds
  return !!(jwtPayload.exp && jwtPayload.exp < now);
}

function decodeAuthToken(token: string = getToken() ?? ''): JWTPayload | null {
  if (token) {
    const decoded = jwtDecode<JWTPayload>(token);
    if (decoded && !isTokenExpired(decoded)) {
      return decoded;
    }
    return null;
  }
  return null;
}

function getUserInfo(): JWTPayload | null {
  const parsedToken = getToken();
  if (!parsedToken) {
    return null;
  }
  return decodeAuthToken(parsedToken);
}

function getToken(): string | null {
  if (isAgentLaunch()) {
    return getAgentToken();
  }

  const token = Cookies.get(AppConfig.JWT_TOKEN_COOKIE_NAME);
  if (token) {
    return token;
  }

  return null;
}

async function getTokenWithRetry(): Promise<string | null> {
  const token = getToken();
  if (token) {
    return token;
  }

  for (let retryCount = 0; retryCount < TOKEN_MAX_RETRIES; retryCount += 1) {
    await new Promise((resolve) => {
      setTimeout(resolve, TOKEN_RETRY_DELAY_MS);
    });

    const retriedToken = getToken();
    if (retriedToken) {
      return retriedToken;
    }
  }

  return null;
}

function verifyTokenAndInit(token: string) {
  const isTokenValid = decodeAuthToken(token);
  if (isTokenValid) {
    Cookies.set(AppConfig.JWT_TOKEN_COOKIE_NAME, token, {
      domain: '.plumhq.com',
    });
  } else {
    window.dev.i('INVALID TOKEN');
  }
}

async function getServicesAPIToken() {
  //Call using API key if the service is being called from server-side
  const { token, sourceHeader, xPlumToken } = isServer()
    ? {
        token: AppConfig.SERVICES_API_KEY,
        sourceHeader: 'plumServerSide',
        xPlumToken: undefined,
      }
    : {
        token: await getIdTokenForCurrentUser(),
        sourceHeader: 'plumClientSide',
        xPlumToken: getToken() ?? undefined,
      };
  return { token, sourceHeader, xPlumToken };
}

function getUserScopedKey(key: string): string {
  const agentSession = getAgentSession();
  if (agentSession) return `${key}:${agentSession.subjectD2cUserId}`;

  const userInfo = getUserInfo();
  if (!userInfo?.memberId) return key;
  return `${key}:${userInfo.memberId}`;
}

export {
  getUserInfo,
  getToken,
  getTokenWithRetry,
  decodeAuthToken,
  isTokenExpired,
  verifyTokenAndInit,
  getServicesAPIToken,
  isServer,
  getIdTokenForCurrentUser,
  getCookie,
  getXPlumTokenCookieNameByEnv,
  getUserScopedKey,
};
